import {
    useAppDisplayModeContext,
    useStyles,
    useTheme,
    Flex,
    Button,
} from '@archipro-design/aria';
import { useRef, useEffect, useState } from 'react';
import type { TagType } from '@modules/product-tagging/type';
import { getTagPosition } from '../../util/get-tag-position';
import type { Image } from '~/modules/page-builder/type/message-passing';
import ProductTagIndicator, {
    INITIAL_TAG_REVEAL_TIMEOUT,
} from '@modules/product-tagging/components/product-tags-display/ProductTagIndicator';
import * as S from './ProductTagsDisplay.style';
import { getTagTooltipDirection } from '@modules/product-tagging/util/get-tag-tooltip-direction';
import { Tag as TagIcon } from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';

interface ProductTagsDisplayProps {
    libraryImage: Image;
    tags: TagType[];
    onToggle?: (toggled: boolean) => void;
}

export const TAG_TOOLTIP_MAX_WIDTH = 352;
const TAG_TOOLTIP_MAX_HEIGHT = 99;

const ProductTagsDisplay = ({
    libraryImage,
    tags,
    onToggle,
}: ProductTagsDisplayProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const { mobile } = useAppDisplayModeContext();
    const [revealDots, setRevealDots] = useState(false);
    const [revealTags, setRevealTags] = useState(false);
    const [forceReveal, setForceReveal] = useState(false);
    const [showToggle, setShowToggle] = useState(false);
    const { css } = useStyles({ showToggle, forceReveal });
    const theme = useTheme();
    const tracker = useTracker();

    useEffect(() => {
        const { current: refCurrent } = ref;
        if (refCurrent) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (mobile) {
                            setRevealDots(entry.isIntersecting);
                        } else {
                            setRevealTags(entry.isIntersecting);
                            setShowToggle(!entry.isIntersecting);
                            if (!entry.isIntersecting) {
                                setForceReveal(false);
                            }
                        }
                    });
                },
                { threshold: 0 },
            );

            observer.observe(refCurrent);
            return () => {
                observer.unobserve(refCurrent);
            };
        }
    }, [ref, mobile]);

    useEffect(() => {
        if (!showToggle) {
            const timeout = setTimeout(() => {
                setShowToggle(true);
            }, INITIAL_TAG_REVEAL_TIMEOUT);

            return () => clearTimeout(timeout);
        }
    }, [showToggle]);

    return (
        <div
            ref={ref}
            className={css(S.TagContainer)}
            onMouseOver={() => setRevealDots(true)}
            onMouseLeave={() => setRevealDots(false)}
        >
            {tags.map((tag) => {
                const { pctAfterOffset } = getTagPosition(
                    tag.positionX,
                    tag.positionY,
                    libraryImage,
                    ref.current?.clientWidth || 0,
                    ref.current?.clientHeight || 0,
                );

                const tooltipDirection = getTagTooltipDirection(
                    tag.positionX,
                    tag.positionY,
                    libraryImage,
                    TAG_TOOLTIP_MAX_WIDTH,
                    TAG_TOOLTIP_MAX_HEIGHT,
                );
                return tag.product ? (
                    <ProductTagIndicator
                        key={tag.id}
                        tagPosition={pctAfterOffset}
                        tooltipDirection={tooltipDirection}
                        taggedProduct={tag.product}
                        revealDots={revealDots}
                        revealTags={revealTags}
                        forceReveal={forceReveal}
                    />
                ) : null;
            })}
            {!mobile && (
                <Flex
                    className={css(S.TagsToggle)}
                    onMouseEnter={() => {
                        tracker.log('TagsButtonHoverReveal', {
                            targetTracker: ['archiproTracker', 'ga4Tracker'],
                            url: new URL(window.location.href),
                        });
                    }}
                    onClick={() => {
                        const toggled = !forceReveal;
                        setForceReveal(toggled);
                        onToggle?.(toggled);
                        tracker.log('TagsButtonClick', {
                            targetTracker: ['archiproTracker', 'ga4Tracker'],
                            url: new URL(window.location.href),
                            data: {
                                ExtraData: toggled,
                            },
                            ga4Data: {
                                toggled: toggled,
                            },
                        });
                    }}
                >
                    <Button
                        size={18}
                        variables={{
                            buttonPadding: 10,
                            iconPadding: 0,
                            buttonRadius: '50%',
                            primaryButtonBackgroundColorNormal:
                                theme.siteVariables.colors.primitive.white,
                            primaryButtonBackgroundColorHover:
                                theme.siteVariables.colors.primitive.white,
                        }}
                        icon={
                            <TagIcon
                                color={
                                    theme.siteVariables.colors.primitive.black
                                }
                            />
                        }
                        styles={{
                            zIndex: theme.siteVariables.zIndexes.menuItem,
                        }}
                    />
                    <div className={css(S.TagsToggleText)}>
                        {forceReveal ? 'Hide' : 'Show'} tagged products (
                        {tags.length})
                    </div>
                </Flex>
            )}
        </div>
    );
};

export default ProductTagsDisplay;
